import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Form, Modal, Table, Spinner, Alert, Row, Col } from 'react-bootstrap';
import {
  useGetTransitionQuery, useUpdateTransitionPlanMutation, useSaveTransitionItemMutation,
  useDeleteTransitionItemMutation, useReorderTransitionItemsMutation,
} from '../../app/api';
import { brand, cardStyle, headStyle, EntPage, StageBadge } from './common';
import TransitionDashboard, { parseDay, shortDate } from '../../components/TransitionDashboard';

/* Entrepreneur -> Transitions -> a firm: where the Transition Manager keeps a
 * transitioning firm's dashboard up to date. Every section maps to one block
 * of the page the firm sees, and "Preview" renders that page from the same
 * data with the same component (components/TransitionDashboard), so what staff
 * check here is exactly what the firm gets.
 *
 * Business contacts, dates and totals only. Nothing typed here should name a
 * client - the readiness panel takes counts, never rows. */

// FastAPI validation errors arrive as a list of {loc, msg}.
const errMsg = (err, fallback) => {
  const d = err?.data?.detail;
  if (Array.isArray(d)) return d.map(x => `${x.loc?.[x.loc.length - 1] || ''} ${x.msg}`.trim()).join('; ');
  return d || fallback;
};

const STATUS_OPTIONS = [['on_track', 'On track'], ['attention', 'Needs attention'], ['at_risk', 'At risk']];
const TASK_STATUS_OPTIONS = [['not_started', 'Not started'], ['in_progress', 'In progress'], ['done', 'Done']];

// One card per list on the dashboard. `fields` names the inputs the editor
// shows for that kind, with the help text staff see; `sortable` lists keep
// the order staff give them, the rest are shown by date as on the firm's page.
const ENTRY_KINDS = [
  { kind: 'week', title: 'Your things this week', sortable: true,
    hint: 'What the firm should do this week - three at most reads best. The "Later" line is under Overview.',
    fields: { label: 'When (e.g. "Tue 10/20, 2:00 pm" or "All week")', title: 'What', detail: 'One line of detail' } },
  { kind: 'milestone', title: 'Timeline milestones',
    hint: 'Dots on the timeline between today and Transition Day. Dates already past drop off by themselves.',
    fields: { title: 'Milestone', entry_date: 'Date', label: 'Note after the date (e.g. "(proposed)")' } },
  { kind: 'waiting', title: 'Waiting on',
    fields: { entry_date: 'Due', label: 'Or a word instead of a date (e.g. "Open")', title: 'What', detail: 'Who, and why it matters' } },
  { kind: 'decision', title: 'Decided recently',
    fields: { entry_date: 'Decided on', title: 'Decision' } },
  { kind: 'event', title: 'Coming up',
    hint: 'Dated items drop off the day after.',
    fields: { entry_date: 'Date', label: 'Or a label instead (e.g. "Tue 10/27")', title: 'What' } },
  { kind: 'contact', title: 'Who to call', sortable: true,
    hint: 'The first contact is named in the page footer ("Tell Chris").',
    fields: { title: 'Name', detail: 'Role, and what to call them about' } },
  { kind: 'link', title: 'Learn and reference', sortable: true,
    hint: 'Link to the live document (Box, SharePoint) rather than a copy.',
    fields: { title: 'Title', detail: 'What it is and when to use it', url: 'Web address (https://...)', label: 'Tag (e.g. "Supports: Prepare")' } },
];
const STAGE_KIND = {
  kind: 'stage', title: 'Stages', sortable: true,
  hint: 'The "Where you are" bar. Choose the current one under Overview; the text below a stage shows while it is still ahead (e.g. "11/17 to 1/15").',
  fields: { title: 'Stage name', label: 'Text below it (dates)' },
};

// Typing "27" into a date box gives the year 0027; min makes the browser refuse
// it before it is saved (the server refuses it too).
const MIN_DATE = '2000-01-01';
const MAX_DATE = '2099-12-31';

const byDate = (a, b) => (parseDay(a.entry_date) || 0) - (parseDay(b.entry_date) || 0);
const blankToNull = (v) => (typeof v === 'string' && v.trim() === '' ? null : v);
const intOrNull = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10));

function moveIds(list, index, dir) {
  const ids = list.map(x => x.id);
  const j = index + dir;
  if (j < 0 || j >= ids.length) return null;
  [ids[index], ids[j]] = [ids[j], ids[index]];
  return ids;
}

const iconBtn = { padding: '0 0.3rem', color: brand.charcoal, textDecoration: 'none' };

function UpDown({ index, count, onMove }) {
  return (
    <>
      <Button variant="link" size="sm" style={iconBtn} disabled={index === 0} onClick={() => onMove(index, -1)} title="Move up" aria-label="Move up">
        <i className="bi bi-arrow-up" />
      </Button>
      <Button variant="link" size="sm" style={iconBtn} disabled={index === count - 1} onClick={() => onMove(index, 1)} title="Move down" aria-label="Move down">
        <i className="bi bi-arrow-down" />
      </Button>
    </>
  );
}
UpDown.propTypes = { index: PropTypes.number.isRequired, count: PropTypes.number.isRequired, onMove: PropTypes.func.isRequired };

// ── Overview + readiness (the plan row) ──────────────────────────────────────
const PLAN_TEXT = ['status_headline', 'status_detail', 'later_note', 'readiness_note'];
const PLAN_INT = ['accounts_entered', 'accounts_expected', 'checks_passed_pct', 'open_exceptions'];

function planToForm(plan) {
  const f = { transition_date: plan.transition_date || '', status: plan.status, current_stage: plan.current_stage };
  PLAN_TEXT.forEach(k => { f[k] = plan[k] || ''; });
  PLAN_INT.forEach(k => { f[k] = plan[k] ?? ''; });
  return f;
}

function PlanCard({ firmId, plan, stages, onSaved, onError }) {
  const [form, setForm] = useState(() => planToForm(plan));
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [update] = useUpdateTransitionPlanMutation();
  // Seeded once and NOT re-seeded when the page refetches: every edit in the
  // other sections refetches, and re-seeding would wipe overview changes that
  // haven't been saved yet.

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setSaving(true); onError(null);
    const body = { firm_id: firmId, transition_date: form.transition_date || null, status: form.status,
      current_stage: intOrNull(form.current_stage) };
    PLAN_TEXT.forEach(k => { body[k] = blankToNull(form[k]); });
    PLAN_INT.forEach(k => { body[k] = intOrNull(form[k]); });
    try {
      await update(body).unwrap();
      setSavedAt(new Date());
      onSaved();
    } catch (err) { onError(errMsg(err, 'Could not save the overview.')); }
    setSaving(false);
  };

  return (
    <Card style={{ ...cardStyle, marginBottom: '1.25rem' }}>
      <Card.Header style={headStyle}>Overview</Card.Header>
      <Card.Body>
        <Form onSubmit={save}>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label>Transition Day</Form.Label>
              <Form.Control type="date" min={MIN_DATE} max={MAX_DATE} value={form.transition_date} onChange={set('transition_date')} />
            </Col>
            <Col md={4}>
              <Form.Label>Status</Form.Label>
              <Form.Select value={form.status} onChange={set('status')}>
                {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Current stage</Form.Label>
              <Form.Select value={form.current_stage} onChange={set('current_stage')}>
                {stages.map((s, i) => <option key={s.id} value={i + 1}>{i + 1} {s.title}</option>)}
                {stages.length === 0 && <option value={1}>Add stages below</option>}
              </Form.Select>
            </Col>
            <Col md={12}>
              <Form.Label>Status headline</Form.Label>
              <Form.Control value={form.status_headline} onChange={set('status_headline')} maxLength={255}
                placeholder="e.g. Two areas need attention this week." />
            </Col>
            <Col md={12}>
              <Form.Label>Status detail</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.status_detail} onChange={set('status_detail')}
                placeholder="e.g. Eight items are due 10/15 or 10/16. None are yours. Your next big date is the first spreadsheet copy on 11/13." />
            </Col>
            <Col md={12}>
              <Form.Label>&quot;Later&quot; line under this week&apos;s things</Form.Label>
              <Form.Control value={form.later_note} onChange={set('later_note')} maxLength={500}
                placeholder="e.g. Later: IT support decision (10/31), first spreadsheet copy (11/13). You don't need to act on these yet." />
            </Col>
          </Row>

          <div style={{ fontWeight: 700, color: brand.deepBlue, margin: '1.25rem 0 0.25rem' }}>Spreadsheet readiness</div>
          <div style={{ fontSize: '0.8rem', color: brand.charcoal, marginBottom: '0.6rem' }}>
            Totals only - never client names or rows. The panel stays hidden until at least one figure is entered.
          </div>
          <Row className="g-3">
            <Col xs={6} md={3}>
              <Form.Label>Accounts entered</Form.Label>
              <Form.Control type="number" min={0} value={form.accounts_entered} onChange={set('accounts_entered')} />
            </Col>
            <Col xs={6} md={3}>
              <Form.Label>Of about</Form.Label>
              <Form.Control type="number" min={0} value={form.accounts_expected} onChange={set('accounts_expected')} />
            </Col>
            <Col xs={6} md={3}>
              <Form.Label>% passed custodian checks</Form.Label>
              <Form.Control type="number" min={0} max={100} value={form.checks_passed_pct} onChange={set('checks_passed_pct')} />
            </Col>
            <Col xs={6} md={3}>
              <Form.Label>Open exceptions</Form.Label>
              <Form.Control type="number" min={0} value={form.open_exceptions} onChange={set('open_exceptions')} />
            </Col>
            <Col md={12}>
              <Form.Label>Note under the figures</Form.Label>
              <Form.Control value={form.readiness_note} onChange={set('readiness_note')} maxLength={500}
                placeholder="e.g. The error rate from Test 1 to Test 2 is the number we watch most." />
            </Col>
          </Row>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem' }}>
            <Button type="submit" disabled={saving} style={{ background: brand.deepBlue, border: 'none' }}>
              {saving ? 'Saving...' : 'Save overview'}
            </Button>
            {savedAt && <span style={{ fontSize: '0.8rem', color: brand.green }}>Saved {savedAt.toLocaleTimeString()}</span>}
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}
PlanCard.propTypes = {
  firmId: PropTypes.number.isRequired, plan: PropTypes.object.isRequired, stages: PropTypes.array.isRequired,
  onSaved: PropTypes.func.isRequired, onError: PropTypes.func.isRequired,
};

// ── Generic list sections (stages, this week, contacts, links, ...) ──────────
function EntryCard({ config, items, onEdit, onDelete, onMove }) {
  const { title, hint, sortable, fields } = config;
  const shown = sortable ? items : [...items].sort(config.kind === 'decision' ? (a, b) => byDate(b, a) : byDate);
  return (
    <Card style={{ ...cardStyle, marginBottom: '1.25rem' }}>
      <Card.Header style={{ ...headStyle, display: 'flex', alignItems: 'center' }}>
        <span style={{ flex: 1 }}>{title}</span>
        <Button size="sm" variant="outline-primary" onClick={() => onEdit(config, null)}>
          <i className="bi bi-plus-lg me-1" />Add
        </Button>
      </Card.Header>
      <Card.Body style={{ padding: shown.length ? 0 : undefined }}>
        {hint && <div style={{ fontSize: '0.8rem', color: brand.charcoal, padding: shown.length ? '0.6rem 1rem 0' : 0, marginBottom: shown.length ? 0 : '0.4rem' }}>{hint}</div>}
        {shown.length === 0 ? (
          <div style={{ color: brand.charcoal, fontSize: '0.85rem' }}>Nothing here yet - this section is hidden from the firm until something is added.</div>
        ) : (
          <Table className="mb-0" style={{ fontSize: '0.86rem' }}>
            <tbody>
              {shown.map((e, i) => {
                const when = e.label || (e.entry_date ? shortDate(parseDay(e.entry_date), new Date(0)) : '');
                return (
                  <tr key={e.id}>
                    {(fields.entry_date || fields.label) && config.kind !== 'link' && (
                      <td style={{ whiteSpace: 'nowrap', fontWeight: 700, color: brand.deepBlue, width: 1, paddingLeft: '1rem' }}>
                        {config.kind === 'stage' ? `${i + 1}` : when}
                      </td>
                    )}
                    <td style={{ paddingLeft: config.kind === 'link' || !(fields.entry_date || fields.label) ? '1rem' : undefined }}>
                      <div style={{ fontWeight: 600, color: brand.deepBlue }}>{e.title}</div>
                      {config.kind === 'stage' && e.label && <div style={{ color: brand.charcoal, fontSize: '0.8rem' }}>{e.label}</div>}
                      {e.detail && <div style={{ color: brand.charcoal, fontSize: '0.8rem' }}>{e.detail}</div>}
                      {e.url && <div style={{ fontSize: '0.78rem', wordBreak: 'break-all' }}><a href={e.url} target="_blank" rel="noopener noreferrer">{e.url}</a></div>}
                      {config.kind === 'link' && e.label && <div style={{ color: brand.charcoal, fontSize: '0.78rem' }}>{e.label}</div>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right', width: 1, paddingRight: '0.75rem' }}>
                      {sortable && <UpDown index={i} count={shown.length} onMove={(idx, dir) => onMove(shown, idx, dir)} />}
                      <Button variant="link" size="sm" style={iconBtn} onClick={() => onEdit(config, e)} title="Edit" aria-label="Edit">
                        <i className="bi bi-pencil" />
                      </Button>
                      <Button variant="link" size="sm" style={{ ...iconBtn, color: brand.red }} onClick={() => onDelete(e)} title="Delete" aria-label="Delete">
                        <i className="bi bi-trash" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}
EntryCard.propTypes = {
  config: PropTypes.object.isRequired, items: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired, onDelete: PropTypes.func.isRequired, onMove: PropTypes.func.isRequired,
};

function EntryModal({ config, item, saving, onSave, onClose }) {
  const [form, setForm] = useState({
    title: item?.title || '', detail: item?.detail || '', label: item?.label || '',
    entry_date: item?.entry_date || '', url: item?.url || '',
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const f = config.fields;
  const submit = (e) => {
    e.preventDefault();
    const body = { title: form.title };
    ['detail', 'label', 'entry_date', 'url'].forEach(k => { body[k] = f[k] ? blankToNull(form[k]) : null; });
    onSave(body);
  };
  // Inputs in the order the fields config names them.
  const input = (k) => {
    if (k === 'title') return <Form.Control required autoFocus value={form.title} onChange={set('title')} maxLength={255} />;
    if (k === 'detail') return <Form.Control as="textarea" rows={2} value={form.detail} onChange={set('detail')} />;
    if (k === 'label') return <Form.Control value={form.label} onChange={set('label')} maxLength={120} />;
    if (k === 'entry_date') return <Form.Control type="date" min={MIN_DATE} max={MAX_DATE} required={config.kind === 'milestone'} value={form.entry_date} onChange={set('entry_date')} />;
    return <Form.Control type="url" required={config.kind === 'link'} placeholder="https://" value={form.url} onChange={set('url')} />;
  };
  return (
    <Modal show onHide={onClose} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.05rem', color: brand.deepBlue }}>{item ? 'Edit' : 'Add to'} {config.title.toLowerCase()}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {Object.entries(f).map(([k, label]) => (
            <Form.Group key={k} className="mb-3">
              <Form.Label style={{ fontSize: '0.85rem' }}>{label}</Form.Label>
              {input(k)}
            </Form.Group>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} style={{ background: brand.deepBlue, border: 'none' }}>{saving ? 'Saving...' : 'Save'}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
EntryModal.propTypes = {
  config: PropTypes.object.isRequired, item: PropTypes.object, saving: PropTypes.bool,
  onSave: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired,
};

// ── Areas and their items ────────────────────────────────────────────────────
function TaskModal({ areas, task, defaultAreaId, firmName, saving, onSave, onClose }) {
  const [form, setForm] = useState({
    area_id: task?.area_id || defaultAreaId || areas[0]?.id || '',
    title: task?.title || '', owner_name: task?.owner_name || '', side: task?.side || 'pca',
    due_date: task?.due_date || '', status: task?.status || 'not_started',
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    onSave({ ...form, area_id: Number(form.area_id), owner_name: blankToNull(form.owner_name), due_date: form.due_date || null });
  };
  return (
    <Modal show onHide={onClose} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.05rem', color: brand.deepBlue }}>{task ? 'Edit item' : 'Add an item'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.85rem' }}>Item</Form.Label>
            <Form.Control required autoFocus value={form.title} onChange={set('title')} maxLength={255} />
          </Form.Group>
          <Row className="g-3 mb-3">
            <Col sm={6}>
              <Form.Label style={{ fontSize: '0.85rem' }}>Area</Form.Label>
              <Form.Select value={form.area_id} onChange={set('area_id')}>
                {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Form.Select>
            </Col>
            <Col sm={6}>
              <Form.Label style={{ fontSize: '0.85rem' }}>Whose side</Form.Label>
              <Form.Select value={form.side} onChange={set('side')}>
                <option value="firm">{firmName}</option>
                <option value="pca">Prosperity</option>
                <option value="joint">Shared</option>
              </Form.Select>
            </Col>
            <Col sm={6}>
              <Form.Label style={{ fontSize: '0.85rem' }}>Owner</Form.Label>
              <Form.Control value={form.owner_name} onChange={set('owner_name')} maxLength={120} placeholder="e.g. Kalem" />
            </Col>
            <Col sm={6}>
              <Form.Label style={{ fontSize: '0.85rem' }}>Due</Form.Label>
              <Form.Control type="date" min={MIN_DATE} max={MAX_DATE} value={form.due_date} onChange={set('due_date')} />
            </Col>
            <Col sm={6}>
              <Form.Label style={{ fontSize: '0.85rem' }}>Status</Form.Label>
              <Form.Select value={form.status} onChange={set('status')}>
                {TASK_STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Form.Select>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} style={{ background: brand.deepBlue, border: 'none' }}>{saving ? 'Saving...' : 'Save'}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
TaskModal.propTypes = {
  areas: PropTypes.array.isRequired, task: PropTypes.object, defaultAreaId: PropTypes.number,
  firmName: PropTypes.string, saving: PropTypes.bool, onSave: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired,
};

const SIDE_LABEL = { pca: 'Prosperity', joint: 'Shared' };

function AreasCard({ areas, tasks, firmName, onAddArea, onRenameArea, onDeleteArea, onMoveArea, onEditTask, onDeleteTask, onTaskStatus }) {
  return (
    <Card style={{ ...cardStyle, marginBottom: '1.25rem' }}>
      <Card.Header style={{ ...headStyle, display: 'flex', alignItems: 'center' }}>
        <span style={{ flex: 1 }}>Progress by area</span>
        <Button size="sm" variant="outline-primary" onClick={onAddArea}><i className="bi bi-plus-lg me-1" />Add area</Button>
      </Card.Header>
      <Card.Body>
        <div style={{ fontSize: '0.8rem', color: brand.charcoal, marginBottom: '0.75rem' }}>
          Each area is a card on the firm&apos;s page; its items also make up the &quot;Every open item&quot; table.
          Progress, &quot;Next&quot; and overdue flags are worked out from the due dates and statuses here.
        </div>
        {areas.length === 0 && <div style={{ color: brand.charcoal, fontSize: '0.85rem' }}>No areas yet.</div>}
        {areas.map((a, i) => {
          const items = tasks.filter(t => t.area_id === a.id);
          return (
            <div key={a.id} style={{ border: `1px solid ${brand.softGray}`, borderRadius: 8, marginBottom: '0.9rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 0.75rem', background: '#faf9f7', borderBottom: items.length ? `1px solid ${brand.softGray}` : 'none' }}>
                <span style={{ flex: 1, fontWeight: 700, color: brand.deepBlue }}>
                  {a.name} <span style={{ fontWeight: 400, color: brand.charcoal, fontSize: '0.8rem' }}>({items.filter(t => t.status === 'done').length} of {items.length} done)</span>
                </span>
                <UpDown index={i} count={areas.length} onMove={onMoveArea} />
                <Button variant="link" size="sm" style={iconBtn} onClick={() => onRenameArea(a)} title="Rename" aria-label="Rename area"><i className="bi bi-pencil" /></Button>
                <Button variant="link" size="sm" style={{ ...iconBtn, color: brand.red }} onClick={() => onDeleteArea(a)} title="Delete area" aria-label="Delete area"><i className="bi bi-trash" /></Button>
                <Button size="sm" variant="link" style={{ textDecoration: 'none', fontSize: '0.8rem' }} onClick={() => onEditTask(null, a.id)}>
                  <i className="bi bi-plus-lg me-1" />Item
                </Button>
              </div>
              {items.length > 0 && (
                <Table responsive className="mb-0" style={{ fontSize: '0.84rem' }}>
                  <tbody>
                    {items.map(t => (
                      <tr key={t.id}>
                        <td style={{ paddingLeft: '0.9rem' }}>
                          <div style={{ fontWeight: 600, color: brand.deepBlue, textDecoration: t.status === 'done' ? 'line-through' : 'none' }}>{t.title}</div>
                          <div style={{ color: brand.charcoal, fontSize: '0.78rem' }}>
                            {[t.owner_name, SIDE_LABEL[t.side] || firmName].filter(Boolean).join(' · ')}
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap', color: brand.charcoal, width: 1 }}>
                          {t.due_date ? `due ${shortDate(parseDay(t.due_date), new Date(0))}` : 'no date'}
                        </td>
                        <td style={{ width: 150 }}>
                          <Form.Select size="sm" value={t.status} onChange={e => onTaskStatus(t, e.target.value)} aria-label="Status">
                            {TASK_STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </Form.Select>
                        </td>
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right', width: 1 }}>
                          <Button variant="link" size="sm" style={iconBtn} onClick={() => onEditTask(t)} title="Edit" aria-label="Edit item"><i className="bi bi-pencil" /></Button>
                          <Button variant="link" size="sm" style={{ ...iconBtn, color: brand.red }} onClick={() => onDeleteTask(t)} title="Delete" aria-label="Delete item"><i className="bi bi-trash" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
}
AreasCard.propTypes = {
  areas: PropTypes.array.isRequired, tasks: PropTypes.array.isRequired, firmName: PropTypes.string,
  onAddArea: PropTypes.func.isRequired, onRenameArea: PropTypes.func.isRequired, onDeleteArea: PropTypes.func.isRequired,
  onMoveArea: PropTypes.func.isRequired, onEditTask: PropTypes.func.isRequired, onDeleteTask: PropTypes.func.isRequired,
  onTaskStatus: PropTypes.func.isRequired,
};

// ── Page ─────────────────────────────────────────────────────────────────────
export default function TransitionEditor() {
  const { firmId: firmIdParam } = useParams();
  const firmId = Number(firmIdParam);
  const navigate = useNavigate();
  const { data, isLoading, error: loadError, refetch } = useGetTransitionQuery({ firm_id: firmId }, { refetchOnMountOrArgChange: true });
  const [saveItem] = useSaveTransitionItemMutation();
  const [deleteItem] = useDeleteTransitionItemMutation();
  const [reorder] = useReorderTransitionItemsMutation();

  const [preview, setPreview] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [entryModal, setEntryModal] = useState(null);   // { config, item }
  const [taskModal, setTaskModal] = useState(null);     // { task, areaId }

  const run = async (fn, fallback) => {
    setError(null);
    try { await fn(); await refetch(); return true; }
    catch (err) { setError(errMsg(err, fallback)); return false; }
  };

  const saveEntry = async (body) => {
    const { config, item } = entryModal;
    setSaving(true);
    const ok = await run(() => saveItem({
      firm_id: firmId, collection: 'entries', id: item?.id, ...(item ? {} : { kind: config.kind }), ...body,
    }).unwrap(), 'Could not save.');
    setSaving(false);
    if (ok) setEntryModal(null);
  };
  const deleteEntry = (e) => {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    run(() => deleteItem({ firm_id: firmId, collection: 'entries', id: e.id }).unwrap(), 'Could not delete.');
  };
  const moveEntry = (list, index, dir) => {
    const ids = moveIds(list, index, dir);
    if (ids) run(() => reorder({ firm_id: firmId, what: 'entries', ids }).unwrap(), 'Could not reorder.');
  };

  const addArea = () => {
    const name = window.prompt('Name of the new area');
    if (name && name.trim()) run(() => saveItem({ firm_id: firmId, collection: 'areas', name }).unwrap(), 'Could not add the area.');
  };
  const renameArea = (a) => {
    const name = window.prompt('Rename area', a.name);
    if (name && name.trim() && name !== a.name) run(() => saveItem({ firm_id: firmId, collection: 'areas', id: a.id, name }).unwrap(), 'Could not rename the area.');
  };
  const deleteArea = (a) => {
    if (!window.confirm(`Delete the area "${a.name}"?`)) return;
    run(() => deleteItem({ firm_id: firmId, collection: 'areas', id: a.id }).unwrap(), 'Could not delete the area.');
  };
  const moveArea = (index, dir) => {
    const ids = moveIds(data.areas, index, dir);
    if (ids) run(() => reorder({ firm_id: firmId, what: 'areas', ids }).unwrap(), 'Could not reorder.');
  };
  const saveTask = async (body) => {
    setSaving(true);
    const ok = await run(() => saveItem({ firm_id: firmId, collection: 'tasks', id: taskModal.task?.id, ...body }).unwrap(), 'Could not save the item.');
    setSaving(false);
    if (ok) setTaskModal(null);
  };
  const deleteTask = (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    run(() => deleteItem({ firm_id: firmId, collection: 'tasks', id: t.id }).unwrap(), 'Could not delete the item.');
  };
  const taskStatus = (t, status) => {
    const { area_id, title, owner_name, side, due_date } = t;
    run(() => saveItem({ firm_id: firmId, collection: 'tasks', id: t.id, area_id, title, owner_name, side, due_date, status }).unwrap(),
      'Could not update the status.');
  };

  const back = (
    <>
      <Button variant="outline-light" size="sm" onClick={() => navigate('/entrepreneur/transitions')}>
        <i className="bi bi-arrow-left me-1" />All transitions
      </Button>
      <Button variant="outline-light" size="sm" onClick={() => navigate(`/entrepreneur/firms/${firmId}`)}>
        <i className="bi bi-building me-1" />Firm &amp; members
      </Button>
    </>
  );

  if (isLoading) return <EntPage title="Transition" actions={back}><div className="text-center p-5"><Spinner /></div></EntPage>;
  if (!data) {
    return (
      <EntPage title="Transition" actions={back}>
        <Card style={cardStyle}><Card.Body style={{ color: brand.charcoal }}>
          {errMsg(loadError, 'This firm has no transition plan.')} Mark the firm as transitioning on its Firms page to start one.
        </Card.Body></Card>
      </EntPage>
    );
  }

  const stages = data.entries.stage || [];
  const live = data.stage !== 'transitioning';
  const actions = (
    <>
      {back}
      <Button size="sm" style={{ background: brand.goldenrod, border: 'none', color: brand.deepBlue, fontWeight: 700 }}
        onClick={() => setPreview(p => !p)}>
        <i className={`bi bi-${preview ? 'pencil-square' : 'eye'} me-1`} />{preview ? 'Back to editing' : 'Preview as the firm'}
      </Button>
    </>
  );

  return (
    <EntPage title={`${data.firm_name} transition`}
      subtitle={live ? 'This firm has gone live. The plan is kept as the record of its transition.' : 'What the firm sees on its transition dashboard. Changes show on their next page load.'}
      actions={actions}>
      {!live && <div style={{ marginTop: '-0.5rem', marginBottom: '0.75rem' }}><StageBadge stage={data.stage} /></div>}
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {preview ? (
        <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 12px rgba(10,41,71,0.12)' }}>
          <TransitionDashboard data={data} />
        </div>
      ) : (
        <>
          <PlanCard firmId={firmId} plan={data.plan} stages={stages} onSaved={refetch} onError={setError} />
          <EntryCard config={STAGE_KIND} items={stages} onEdit={(config, item) => setEntryModal({ config, item })}
            onDelete={deleteEntry} onMove={moveEntry} />
          <AreasCard areas={data.areas} tasks={data.tasks} firmName={data.firm_name}
            onAddArea={addArea} onRenameArea={renameArea} onDeleteArea={deleteArea} onMoveArea={moveArea}
            onEditTask={(task, areaId) => setTaskModal({ task, areaId })} onDeleteTask={deleteTask} onTaskStatus={taskStatus} />
          <Row className="g-0" style={{ columnGap: '1.25rem' }}>
            {ENTRY_KINDS.map(config => (
              <Col key={config.kind} lg style={{ minWidth: 340, flexBasis: 'calc(50% - 0.625rem)' }}>
                <EntryCard config={config} items={data.entries[config.kind] || []}
                  onEdit={(cfg, item) => setEntryModal({ config: cfg, item })} onDelete={deleteEntry} onMove={moveEntry} />
              </Col>
            ))}
          </Row>
        </>
      )}

      {entryModal && (
        <EntryModal config={entryModal.config} item={entryModal.item} saving={saving}
          onSave={saveEntry} onClose={() => setEntryModal(null)} />
      )}
      {taskModal && (
        data.areas.length === 0 ? (
          <Modal show onHide={() => setTaskModal(null)} centered>
            <Modal.Body>Add an area first - every item belongs to one.</Modal.Body>
            <Modal.Footer><Button onClick={() => setTaskModal(null)}>OK</Button></Modal.Footer>
          </Modal>
        ) : (
          <TaskModal areas={data.areas} task={taskModal.task} defaultAreaId={taskModal.areaId} firmName={data.firm_name}
            saving={saving} onSave={saveTask} onClose={() => setTaskModal(null)} />
        )
      )}
    </EntPage>
  );
}
